// ────────────────────────────────────────────────────────────────────────────
// A customer's document-layout preferences.
//
// `Party.preferredDocumentLayoutsJson` holds `{ app: { kind: layoutKey } }` —
// keyed by app because a layout belongs to one: layouts are scoped
// (organisation, app, document kind), since the apps print different things. A
// customer invoiced by both TMS and Depot therefore has one choice per app, and
// a single scalar could only ever have held the first of them.
//
// Directory stores the map; each app reads its own branch. This module is the
// TMS side of that contract, and the one place the shape is known — everything
// else deals in "the layout key for this document kind".
// ────────────────────────────────────────────────────────────────────────────
import type { DocumentType } from '@/features/settings/documents/lib/types';

/** Our branch of the map. Matches Directory's `AppName.Tms`. */
export const LAYOUT_PREF_APP = 'Tms';

/** `{ app: { documentKind: layoutKey } }`, as stored. */
export type DocumentLayoutPrefs = Record<string, Record<string, string>>;

/**
 * Parse the stored map, tolerating anything.
 *
 * A preference is a nicety: unparseable or oddly-shaped JSON must degrade to
 * "no preference" — and so print on the organisation's default — rather than
 * break invoicing. Non-string values and non-object branches are dropped for
 * the same reason.
 */
export function parseLayoutPrefs(json: string | null | undefined): DocumentLayoutPrefs {
  if (!json) return {};
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return {};
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};

  const out: DocumentLayoutPrefs = {};
  for (const [app, branch] of Object.entries(raw as Record<string, unknown>)) {
    if (!branch || typeof branch !== 'object' || Array.isArray(branch)) continue;
    const kinds: Record<string, string> = {};
    for (const [kind, key] of Object.entries(branch as Record<string, unknown>)) {
      if (typeof key === 'string' && key.trim()) kinds[kind] = key;
    }
    if (Object.keys(kinds).length > 0) out[app] = kinds;
  }
  return out;
}

/** The layout key this customer wants for `kind`, or null to follow the default. */
export function readLayoutPref(
  json: string | null | undefined,
  kind: DocumentType,
): string | null {
  return parseLayoutPrefs(json)[LAYOUT_PREF_APP]?.[kind] ?? null;
}

/**
 * Set (or clear, with `null`) the key for one document kind, returning the JSON
 * to store — or `null` when nothing is left, so "no preferences" is stored as
 * NULL rather than as `{}`.
 *
 * Merges into the existing map: **other apps' branches must survive**. A TMS
 * screen writing this must not wipe the customer's Depot choice, which is
 * exactly what a naive rewrite of the whole column would do.
 */
export function writeLayoutPref(
  json: string | null | undefined,
  kind: DocumentType,
  key: string | null,
): string | null {
  const prefs = parseLayoutPrefs(json);
  const branch = { ...(prefs[LAYOUT_PREF_APP] ?? {}) };

  if (key && key.trim()) branch[kind] = key.trim();
  else delete branch[kind];

  const next: DocumentLayoutPrefs = { ...prefs };
  if (Object.keys(branch).length > 0) next[LAYOUT_PREF_APP] = branch;
  else delete next[LAYOUT_PREF_APP];

  return Object.keys(next).length > 0 ? JSON.stringify(next) : null;
}

/** Apply both TMS choices at once — what the party form saves. */
export function writeLayoutPrefs(
  json: string | null | undefined,
  choices: Partial<Record<DocumentType, string | null>>,
): string | null {
  let out = json ?? null;
  for (const [kind, key] of Object.entries(choices) as [DocumentType, string | null][]) {
    out = writeLayoutPref(out, kind, key);
  }
  return out;
}
